import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

let ytApiLoaded = false;
let ytApiLoading = false;
const ytReadyCallbacks = [];

function loadYouTubeAPI() {
  if (ytApiLoaded) return Promise.resolve();
  if (ytApiLoading) return new Promise(resolve => ytReadyCallbacks.push(resolve));
  ytApiLoading = true;
  return new Promise(resolve => {
    ytReadyCallbacks.push(resolve);
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    window.onYouTubeIframeAPIReady = () => {
      ytApiLoaded = true;
      ytApiLoading = false;
      ytReadyCallbacks.forEach(cb => cb());
      ytReadyCallbacks.length = 0;
    };
  });
}

export default function PostMoment({ onClose }) {
  const { user, token } = useApp();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [timestamp, setTimestamp] = useState(0);
  const [endTimestamp, setEndTimestamp] = useState(null);
  const [duration, setDuration] = useState(300);
  const [note, setNote] = useState('');
  const [searching, setSearching] = useState(false);
 const [posting, setPosting] = useState(false);
const [tags, setTags] = useState([]);
const [tagInput, setTagInput] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);
  const isDragging = useRef(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      let res;
      try {
        res = await axios.get(`https://songmoment.onrender.com/api/spotify/search?q=${encodeURIComponent(query)}`);
      } catch {
        res = await axios.get(`https://songmoment.onrender.com/api/spotify/mock-search?q=${encodeURIComponent(query)}`);
      }
      setResults(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const initPlayer = (youtubeId) => {
    loadYouTubeAPI().then(() => {
      if (!containerRef.current) return;
      if (playerRef.current?.destroy) playerRef.current.destroy();
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: youtubeId,
        playerVars: {
          autoplay: 0, controls: 0, disablekb: 1,
          fs: 0, modestbranding: 1, rel: 0, showinfo: 0, iv_load_policy: 3,
          playsinline: 1,
        },
        events: {
          onReady: (e) => {
            const dur = e.target.getDuration();
            if (dur > 0) setDuration(dur);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              intervalRef.current = setInterval(() => {
                if (playerRef.current?.getCurrentTime && !isDragging.current) {
                  const t = playerRef.current.getCurrentTime();
                  setCurrentTime(t);
                }
              }, 200);
            } else {
              setIsPlaying(false);
              clearInterval(intervalRef.current);
            }
          },
        },
      });
    });
  };

  const selectTrack = (track) => {
    setSelected(track);
    setTimestamp(0);
    setEndTimestamp(null);
    setCurrentTime(0);
    setDuration(300);
    setIsPlaying(false);
    clearInterval(intervalRef.current);
    if (track.youtube_id) setTimeout(() => initPlayer(track.youtube_id), 100);
  };

  const togglePlay = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.seekTo(currentTime, true);
      playerRef.current.playVideo();
    }
  };

  const handleSliderChange = (e) => {
    isDragging.current = true;
    const newTime = Number(e.target.value);
    setCurrentTime(newTime);
  };

  const handleSliderMouseUp = (e) => {
    isDragging.current = false;
    const newTime = Number(e.target.value);
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(newTime, true);
      if (isPlaying) playerRef.current.playVideo();
    }
  };

  const setStartPoint = () => {
    setTimestamp(Math.floor(currentTime * 1000));
    // Clear end if it's before new start
    if (endTimestamp !== null && endTimestamp <= Math.floor(currentTime * 1000)) {
      setEndTimestamp(null);
    }
  };

  const setEndPoint = () => {
    const endMs = Math.floor(currentTime * 1000);
    if (endMs <= timestamp) {
      alert('End point must be after start point!');
      return;
    }
    setEndTimestamp(endMs);
  };

  const clearEndPoint = () => setEndTimestamp(null);

  const post = async () => {
    if (!selected || !user) return;
    setPosting(true);
    if (playerRef.current?.pauseVideo) playerRef.current.pauseVideo();
    try {
      await axios.post('https://songmoment.onrender.com/api/moments', {
  spotify_track_id: selected.id,
  track_name: selected.name,
  artist_name: selected.artist,
  album_image: selected.album_image,
  timestamp_ms: timestamp,
  end_timestamp_ms: endTimestamp,
  note: note.trim() || null,
  embed_url: selected.embed_url || null,
  preview_url: selected.preview_url || null,
  tags: tags,
}, { headers: { Authorization: `Bearer ${token}` } });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setPosting(false);
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      if (playerRef.current?.destroy) playerRef.current.destroy();
    };
  }, []);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const startPercent = duration > 0 ? (timestamp / 1000 / duration) * 100 : 0;
  const endPercent = duration > 0 && endTimestamp ? (endTimestamp / 1000 / duration) * 100 : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-in" style={{
        background: 'var(--bg2)', border: '1px solid var(--border2)',
        borderRadius: 18, width: '100%', maxWidth: 500,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 0' }}>
          <h2 style={{ fontSize: 18, fontWeight: 800 }}>Share a moment</h2>
          <button onClick={onClose} style={{ fontSize: 20, color: 'var(--text3)', background: 'none', lineHeight: 1, border: 'none', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '16px 20px 20px' }}>
          {/* Step 1: Search */}
          <label style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            1. Find a song
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, marginBottom: 12 }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search()}
              placeholder="Search songs or artists..."
              style={{
                flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '9px 12px', fontSize: 14, color: 'var(--text)',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <button onClick={search} disabled={searching} style={{
              background: 'var(--accent)', color: '#fff', borderRadius: 8,
              padding: '9px 16px', fontSize: 13, fontWeight: 500,
              opacity: searching ? 0.6 : 1, border: 'none', cursor: 'pointer',
            }}>
              {searching ? '...' : 'Search'}
            </button>
          </div>

          {/* Results */}
          {results.length > 0 && !selected && (
            <div style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {results.map((track, i) => (
                <div key={track.id} onClick={() => selectTrack(track)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    cursor: 'pointer', borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none',
                    transition: 'background 0.15s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--bg3)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                >
                  {track.album_image
                    ? <img src={track.album_image} alt="" style={{ width: 36, height: 36, borderRadius: 6 }} />
                    : <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>♪</div>
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{track.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{track.artist}</div>
                  </div>
                  {track.source === 'youtube' && (
                    <span style={{ fontSize: 10, color: 'var(--accent2)', background: 'rgba(124,106,247,0.15)', padding: '2px 6px', borderRadius: 4 }}>
                      ▶ YouTube
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Selected track */}
          {selected && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg3)', borderRadius: 10, marginBottom: 16 }}>
                {selected.album_image
                  ? <img src={selected.album_image} alt="" style={{ width: 40, height: 40, borderRadius: 6 }} />
                  : <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>♪</div>
                }
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>{selected.artist}</div>
                </div>
                <button onClick={() => {
                  setSelected(null);
                  setResults([]);
                  if (playerRef.current?.destroy) playerRef.current.destroy();
                }} style={{ fontSize: 12, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  change
                </button>
              </div>

              {/* Step 2: Pick moment */}
              <label style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 12 }}>
                2. Pick your moment
              </label>

              {/* Hidden YouTube player */}
              <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: 320, height: 180, pointerEvents: 'none' }}>
                <div ref={containerRef} style={{ width: 320, height: 180 }} />
              </div>

              {/* Player UI */}
              <div style={{ background: 'var(--bg3)', borderRadius: 14, padding: 16, marginBottom: 16 }}>

                {/* Timestamps display */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 14 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Start</div>
                    <span style={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>
                      {formatTime(timestamp)}
                    </span>
                  </div>
                  <div style={{ fontSize: 24, color: 'var(--border2)', alignSelf: 'flex-end', paddingBottom: 2 }}>→</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>End</div>
                    <span style={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 700, color: endTimestamp ? '#fff' : 'var(--border2)' }}>
                      {endTimestamp ? formatTime(endTimestamp) : '--:--'}
                    </span>
                  </div>
                </div>

                {/* Progress bar with highlighted region */}
                <div style={{ position: 'relative', marginBottom: 6 }}>
                  <div style={{ height: 6, background: 'var(--border2)', borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                    {/* Highlighted region */}
                    {endPercent !== null && (
                      <div style={{
                        position: 'absolute',
                        left: `${startPercent}%`,
                        width: `${endPercent - startPercent}%`,
                        height: '100%',
                        background: 'rgba(255,255,255,0.9)',
                        boxShadow: '0 0 8px rgba(255,255,255,0.8)',
                        borderRadius: 3,
                        zIndex: 1,
                      }} />
                    )}
                    {/* Playhead */}
                    <div style={{
                      height: '100%', width: `${progressPercent}%`,
                      background: 'var(--accent)', borderRadius: 3,
                      transition: isDragging.current ? 'none' : 'width 0.2s',
                      position: 'relative', zIndex: 2,
                    }} />
                  </div>
                  <input
                    type="range" min="0" max={duration}
                    value={currentTime}
                    onChange={handleSliderChange}
                    onMouseUp={handleSliderMouseUp}
                    onTouchEnd={handleSliderMouseUp}
                    style={{
                      position: 'absolute', top: -8, left: 0,
                      width: '100%', opacity: 0, cursor: 'pointer', height: 24,
                    }}
                  />
                </div>

                {/* Time labels */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>
                  <span>{formatTime(currentTime * 1000)}</span>
                  <span>{formatTime(duration * 1000)}</span>
                </div>

                {/* Play button */}
                {selected.youtube_id && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 16 }}>
                    <button onClick={togglePlay} style={{
                      width: 52, height: 52, borderRadius: '50%',
                      background: 'var(--accent)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, border: 'none', cursor: 'pointer',
                    }}>
                      {isPlaying ? '⏸' : '▶'}
                    </button>
                    <div>
                      <p style={{ fontSize: 13, color: 'var(--text2)', margin: 0, fontWeight: 500 }}>
                        {isPlaying ? 'Playing...' : 'Play to preview'}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text3)', margin: 0 }}>
                        Drag or play to find your moment
                      </p>
                    </div>
                  </div>
                )}

                {/* Set start/end buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={setStartPoint} style={{
                    flex: 1, padding: '8px 0', borderRadius: 8,
                    background: 'rgba(124,106,247,0.15)',
                    border: '1px solid var(--accent)',
                    color: 'var(--accent)', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                    📍 Set start — {formatTime(Math.floor(currentTime) * 1000)}
                  </button>
                  <button onClick={setEndPoint} style={{
                    flex: 1, padding: '8px 0', borderRadius: 8,
                    background: endTimestamp ? 'rgba(255,255,255,0.1)' : 'var(--bg2)',
                    border: endTimestamp ? '1px solid rgba(255,255,255,0.5)' : '1px solid var(--border)',
                    color: endTimestamp ? '#fff' : 'var(--text3)', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                    🏁 Set end — {formatTime(Math.floor(currentTime) * 1000)}
                  </button>
                </div>
                {endTimestamp && (
                  <button onClick={clearEndPoint} style={{
                    width: '100%', marginTop: 6, padding: '5px 0',
                    background: 'none', border: 'none',
                    color: 'var(--text3)', fontSize: 11, cursor: 'pointer',
                  }}>
                    ✕ Remove end point
                  </button>
                )}
              </div>

             {/* Step 3: Tags */}
<label style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 10 }}>
  3. Add tags <span style={{ fontSize: 11 }}>(optional)</span>
</label>

{/* Preset tags */}
<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
  {['#Chill', '#NightDrive', '#CryingInTheCar', '#MainCharacter', '#Heartbreak', '#Hype', '#GoodVibes'].map(preset => (
    <button key={preset} type="button" onClick={() => {
      setTags(prev =>
        prev.includes(preset) ? prev.filter(t => t !== preset) : [...prev, preset]
      );
    }} style={{
      fontSize: 12, padding: '5px 12px', borderRadius: 20,
      background: tags.includes(preset) ? 'rgba(212,83,126,0.2)' : 'var(--bg3)',
      border: `1px solid ${tags.includes(preset) ? 'var(--accent)' : 'var(--border)'}`,
      color: tags.includes(preset) ? 'var(--accent)' : 'var(--text3)',
      cursor: 'pointer', transition: 'all 0.15s',
    }}>
      {preset}
    </button>
  ))}
</div>

{/* Custom tag input */}
<div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
  <input
    value={tagInput}
    onChange={e => setTagInput(e.target.value)}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const val = tagInput.trim().replace(/^#/, '');
        if (val && !tags.includes(`#${val}`) && tags.length < 5) {
          setTags(prev => [...prev, `#${val}`]);
          setTagInput('');
        }
      }
    }}
    placeholder="Type a custom tag + Enter..."
    style={{
      flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text)',
    }}
    onFocus={e => e.target.style.borderColor = 'var(--accent)'}
    onBlur={e => e.target.style.borderColor = 'var(--border)'}
  />
</div>

{/* Selected tags */}
{tags.length > 0 && (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
    {tags.map(tag => (
      <span key={tag} style={{
        fontSize: 12, padding: '4px 10px', borderRadius: 20,
        background: 'rgba(212,83,126,0.15)',
        border: '1px solid var(--accent)',
        color: 'var(--accent)',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {tag}
        <button type="button" onClick={() => setTags(prev => prev.filter(t => t !== tag))}
          style={{ fontSize: 10, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}>
          ✕
        </button>
      </span>
    ))}
  </div>
)}

{/* Step 4: Note */}
<label style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
  4. Why this moment? <span style={{ fontSize: 11 }}>(optional)</span>
</label>
              <textarea value={note} onChange={e => setNote(e.target.value)}
                placeholder="This part hits different because..."
                rows={2}
                style={{
                  width: '100%', marginTop: 6, background: 'var(--bg3)',
                  border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px',
                  fontSize: 14, color: 'var(--text)', resize: 'none', marginBottom: 16,
                  boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />

              <button onClick={post} disabled={posting} style={{
                width: '100%', background: 'var(--accent)', color: '#fff',
                borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 600,
                opacity: posting ? 0.6 : 1, transition: 'opacity 0.2s',
                border: 'none', cursor: 'pointer',
              }}>
                {posting ? 'Sharing...' : `Share moment ${formatTime(timestamp)}${endTimestamp ? ` → ${formatTime(endTimestamp)}` : ''}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}